import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatsGrid } from "../../ui/Data/StatCard";
import ConversationFilters from "../../ui/Conversations/ConversationFilters";
import ConversationCard from "../../ui/Conversations/ConversationCard";
import { ConversationsGridSkeleton } from "../../ui/Conversations/ConversationSkeletons";
import Pagination from "../../ui/Data/Pagination";
import { useConversations } from "@finitless/shared/hooks";
import { useConversationSocketUpdates } from "../../../hooks/useConversationSocketUpdates";
import { useAiChatsMetrics } from "../../../hooks/useDashboardQueries";
import { PageHeader } from "@finitless/shared/components";
import { BusinessDropdown } from "../../ui/Business/BusinessDropdown";
import { Icon } from "../../ui/UIElements/Icon";
import { Button } from "../../ui/UIElements/Button";
import { formatNumberByLocale } from "../../../lib/localeFormatters";
import MonthYearPicker from "../reports/MonthYearPicker";
import { getCurrentMonth } from "../../../lib/reportUtils";
import { getCreatedAtRangeForFilter } from "@finitless/shared/orderConstants";
import "./conversations-pages.css";

const ConversationsPage = () => {
  const { t } = useTranslation("conversations");
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("all");
  const [statusReasonFilter, setStatusReasonFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [filterBusiness, setFilterBusiness] = useState(null);
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] = useState(false);
  const businessDropdownRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const getMonthString = (date) => {
    if (!date) return getCurrentMonth();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const ITEMS_PER_PAGE = 15;
  const MAX_VISIBLE_PAGES = 5;

  // Initialize socket updates for real-time conversation updates
  useConversationSocketUpdates();

  // Close business dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        businessDropdownRef.current &&
        !businessDropdownRef.current.contains(event.target)
      ) {
        setIsBusinessDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleBusinessSelect = (brand) => {
    setFilterBusiness(brand);
    setIsBusinessDropdownOpen(false);
    setCurrentPage(1);
  };

  const handleClearBusinessFilter = () => {
    setFilterBusiness(null);
    setCurrentPage(1);
  };

  // Fetch AI chats metrics
  const chatsMetricsParams = { business_id: filterBusiness?.id || null };
  if (selectedDate) {
    chatsMetricsParams.month = getMonthString(selectedDate);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      chatsMetricsParams.timezone = tz;
    } else {
      chatsMetricsParams.timezone_offset = -(
        new Date().getTimezoneOffset() / 60
      );
    }
  }

  const { data: aiChatsMetricsData, isLoading: metricsLoading } =
    useAiChatsMetrics(chatsMetricsParams);

  // Helper function to format change percentage
  const formatChangePercentage = (change) => {
    if (change === null || change === undefined) return null;
    const isPositive = change >= 0;
    return {
      value: `${isPositive ? "+" : ""}${formatNumberByLocale(change, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`,
      isPositive,
    };
  };

  const createdAtRange = useMemo(
    () => getCreatedAtRangeForFilter(selectedDate),
    [selectedDate],
  );

  // Build query parameters based on filters
  const queryParams = useMemo(() => {
    const params = { page: currentPage };
    const whereConditions = [];

    whereConditions.push(
      {
        attribute: "created_at",
        value: {
          condition: ">=",
          value: createdAtRange.start,
        },
      },
      {
        attribute: "created_at",
        value: {
          condition: "<=",
          value: createdAtRange.end,
        },
      },
    );

    // Only add business_id filter if filterBusiness is set
    if (filterBusiness?.id) {
      whereConditions.push({
        attribute: "business_id",
        value: filterBusiness.id,
      });
    }

    // Add status filter if not 'all'
    if (statusFilter !== "all") {
      whereConditions.push({ attribute: "status", value: statusFilter });
    }

    // Add status_reason filter if not 'all'
    if (statusReasonFilter !== "all") {
      whereConditions.push({
        attribute: "status_reason",
        value: statusReasonFilter,
      });
    }

    params.where = JSON.stringify(whereConditions);
    return params;
  }, [
    statusFilter,
    statusReasonFilter,
    currentPage,
    filterBusiness?.id,
    createdAtRange,
  ]);

  // Fetch conversations
  const {
    data: conversationsData,
    isLoading,
    error,
  } = useConversations(queryParams);

  const conversations = conversationsData?.conversations || [];
  const pagination = conversationsData?.pagination || {};

  // Create metric cards using AI chats metrics data
  const metricCards = useMemo(() => {
    const totalChatsChange = formatChangePercentage(
      aiChatsMetricsData?.total_chats?.change_percentage,
    );
    const completedChatsChange = formatChangePercentage(
      aiChatsMetricsData?.completed_chats?.change_percentage,
    );
    const abandonedChatsChange = formatChangePercentage(
      aiChatsMetricsData?.abandoned_chats?.change_percentage,
    );
    const failedChatsChange = formatChangePercentage(
      aiChatsMetricsData?.failed_chats?.change_percentage,
    );
    const successRateChange = formatChangePercentage(
      aiChatsMetricsData?.success_rate?.change_percentage,
    );

    return [
      {
        id: "ai-total-chats",
        title: t("metrics.totalChats"),
        titleHelp: {
          ariaLabel: t("help.metrics.totalChats.ariaLabel"),
          whatIs: t("help.metrics.totalChats.whatIs"),
          period: t("help.metrics.totalChats.period"),
        },
        value: aiChatsMetricsData?.total_chats?.current ?? 0,
        change: totalChatsChange?.value,
        yesterday: aiChatsMetricsData?.total_chats?.previous ?? 0,
        icon: "MessageCircle",
        iconColor: "text-primary",
        iconBg: "bg-accent",
      },
      {
        id: "ai-active-chats",
        title: t("metrics.activeChats"),
        titleHelp: {
          ariaLabel: t("help.metrics.activeChats.ariaLabel"),
          whatIs: t("help.metrics.activeChats.whatIs"),
          period: t("help.metrics.activeChats.period"),
        },
        value: aiChatsMetricsData?.active_chats?.current ?? 0,
        yesterday: aiChatsMetricsData?.active_chats?.previous ?? 0,
        icon: "Clock",
        iconColor: "text-warning",
        iconBg: "bg-warning-bg",
        valueColor: "text-warning",
      },
      {
        id: "ai-completed-chats",
        title: t("metrics.completed"),
        titleHelp: {
          ariaLabel: t("help.metrics.completed.ariaLabel"),
          whatIs: t("help.metrics.completed.whatIs"),
          period: t("help.metrics.completed.period"),
        },
        value: aiChatsMetricsData?.completed_chats?.current ?? 0,
        change: completedChatsChange?.value,
        yesterday: aiChatsMetricsData?.completed_chats?.previous ?? 0,
        icon: "CheckCircle",
        iconColor: "text-success",
        iconBg: "bg-success-bg",
        valueColor: "text-success",
      },
      {
        id: "ai-abandoned-chats",
        title: t("metrics.abandoned"),
        titleHelp: {
          ariaLabel: t("help.metrics.abandoned.ariaLabel"),
          whatIs: t("help.metrics.abandoned.whatIs"),
          period: t("help.metrics.abandoned.period"),
        },
        value: aiChatsMetricsData?.abandoned_chats?.current ?? 0,
        change: abandonedChatsChange?.value,
        yesterday: aiChatsMetricsData?.abandoned_chats?.previous ?? 0,
        icon: "XCircle",
        iconColor: "text-warning",
        iconBg: "bg-warning-bg",
        valueColor: "text-warning",
        changeColor: abandonedChatsChange?.isPositive
          ? "text-error"
          : "text-success",
      },
      {
        id: "ai-failed-chats",
        title: t("metrics.failed"),
        titleHelp: {
          ariaLabel: t("help.metrics.failed.ariaLabel"),
          whatIs: t("help.metrics.failed.whatIs"),
          period: t("help.metrics.failed.period"),
        },
        value: aiChatsMetricsData?.failed_chats?.current ?? 0,
        change: failedChatsChange?.value,
        yesterday: aiChatsMetricsData?.failed_chats?.previous ?? 0,
        icon: "AlertCircle",
        iconColor: "text-error",
        iconBg: "bg-error-bg",
        valueColor: "text-error",
        changeColor: failedChatsChange?.isPositive
          ? "text-error"
          : "text-success",
      },
      {
        id: "ai-success-rate",
        title: t("metrics.successRate"),
        titleHelp: {
          ariaLabel: t("help.metrics.successRate.ariaLabel"),
          whatIs: t("help.metrics.successRate.whatIs"),
          formula: t("help.metrics.successRate.formula"),
          period: t("help.metrics.successRate.period"),
        },
        value: `${formatNumberByLocale(
          aiChatsMetricsData?.success_rate?.current ?? 0,
          {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          },
        )}%`,
        change: successRateChange?.value,
        yesterday: `${formatNumberByLocale(
          aiChatsMetricsData?.success_rate?.previous ?? 0,
          {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          },
        )}%`,
        icon: "TrendingUp",
        iconColor: "text-primary",
        iconBg: "bg-accent",
        valueColor: "text-primary",
      },
    ];
  }, [aiChatsMetricsData]);

  // Calculate pagination data
  const totalPages = pagination?.total_pages || 1;
  const totalItems = pagination?.total || 0;

  // Handle conversation selection
  const handleViewChat = (conversation) => {
    // Navigate to conversation detail page
    navigate(`/analyze/conversations/${conversation.id}`, {
      state: { conversation },
    });
  };

  const handleViewOrder = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  // Handle filter changes
  const handleStatusFilterChange = (newStatusFilter) => {
    setStatusFilter(newStatusFilter);
    setCurrentPage(1);
  };

  const handleStatusReasonFilterChange = (newStatusReasonFilter) => {
    setStatusReasonFilter(newStatusReasonFilter);
    setCurrentPage(1);
  };

  const handleSearchChange = (term) => {
    setSearchQuery(term);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSelectedDateChange = (date) => {
    setSelectedDate(date);
    setCurrentPage(1);
  };

  return (
    <div
      className="ordering-conversations-page space-y-4 sm:space-y-6"
      data-ordering-surface="conversations-list"
    >
      <PageHeader
        title={
          filterBusiness
            ? t("header.titleWithBusiness", { name: filterBusiness.name })
            : t("header.title")
        }
        subtitle={t("header.subtitle")}
      />

      {/* AI Chats Metrics */}
      <section className="border-border bg-card rounded-lg border p-4 shadow-[var(--od-shadow-1)]">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => handleSelectedDateChange(null)}
              aria-pressed={selectedDate === null}
              className={`focus-visible:ring-ring min-h-11 rounded-md border px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none ${
                selectedDate === null
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground hover:bg-accent"
              }`}
            >
              {t("date.today", { ns: "common" })}
            </Button>
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                const now = new Date();
                handleSelectedDateChange(
                  new Date(now.getFullYear(), now.getMonth(), 1),
                );
              }}
              aria-pressed={Boolean(
                selectedDate &&
                selectedDate.getMonth() === new Date().getMonth() &&
                selectedDate.getFullYear() === new Date().getFullYear(),
              )}
              className={`focus-visible:ring-ring min-h-11 rounded-md border px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none ${
                selectedDate &&
                selectedDate.getMonth() === new Date().getMonth() &&
                selectedDate.getFullYear() === new Date().getFullYear()
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground hover:bg-accent"
              }`}
            >
              {t("date.currentMonth", { ns: "common" })}
            </Button>
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                const now = new Date();
                handleSelectedDateChange(
                  new Date(now.getFullYear(), now.getMonth() - 1, 1),
                );
              }}
              aria-pressed={Boolean(
                selectedDate &&
                selectedDate.getMonth() ===
                  new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 1,
                    1,
                  ).getMonth() &&
                selectedDate.getFullYear() ===
                  new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 1,
                    1,
                  ).getFullYear(),
              )}
              className={`focus-visible:ring-ring min-h-11 rounded-md border px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none ${
                selectedDate &&
                selectedDate.getMonth() ===
                  new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 1,
                    1,
                  ).getMonth() &&
                selectedDate.getFullYear() ===
                  new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 1,
                    1,
                  ).getFullYear()
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground hover:bg-accent"
              }`}
            >
              {t("date.lastMonth", { ns: "common" })}
            </Button>
            <MonthYearPicker
              value={selectedDate}
              onChange={handleSelectedDateChange}
              placeholder={t("date.selectMonth", { ns: "common" })}
              id="conversations-month-picker"
            />
          </div>
        </div>
      </section>
      {metricsLoading ? (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3"
          role="status"
          aria-live="polite"
        >
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="border-border bg-card animate-pulse rounded-lg border p-4 shadow-[var(--od-shadow-1)] motion-reduce:animate-none sm:p-6"
            >
              <div className="bg-muted mb-3 h-3 w-1/2 rounded sm:mb-4 sm:h-4"></div>
              <div className="bg-muted h-6 w-3/4 rounded sm:h-8"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="ordering-conversations-page__stats">
          <StatsGrid stats={metricCards} columns={3} />
        </div>
      )}

      {/* Filters and Search */}
      <section className="border-border bg-card mb-6 rounded-lg border p-4 shadow-[var(--od-shadow-1)]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Left side - Business Dropdown and Filters */}
          <div className="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row">
            {/* Business Filter Dropdown */}
            <div
              className="relative min-w-0 sm:w-64 sm:max-w-full"
              ref={businessDropdownRef}
            >
              <Button
                variant="ghost"
                type="button"
                onClick={() =>
                  setIsBusinessDropdownOpen(!isBusinessDropdownOpen)
                }
                aria-expanded={isBusinessDropdownOpen}
                className="border-border bg-background text-foreground hover:bg-accent focus-visible:ring-ring flex h-auto min-h-11 w-full min-w-0 items-center gap-2 rounded-md border px-3 py-2 whitespace-normal transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
              >
                <span className="min-w-0 text-start [overflow-wrap:anywhere] break-words">
                  {filterBusiness
                    ? filterBusiness.name
                    : t("filters.allBusinesses")}
                </span>
                <Icon
                  name={isBusinessDropdownOpen ? "ChevronUp" : "ChevronDown"}
                  className="text-muted-foreground ms-auto h-4 w-4 flex-shrink-0"
                />
              </Button>
              {filterBusiness && (
                <Button
                  variant="ghost"
                  type="button"
                  onClick={handleClearBusinessFilter}
                  className="border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring absolute -end-3 -top-3 flex min-h-11 min-w-11 items-center justify-center rounded-full border transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none"
                  title={t("filters.clearBusiness")}
                  aria-label={t("filters.clearBusiness")}
                >
                  <Icon name="X" className="h-3.5 w-3.5" />
                </Button>
              )}
              <div className="absolute start-0 top-full z-[var(--layer-dropdown)] mt-2">
                <BusinessDropdown
                  isOpen={isBusinessDropdownOpen}
                  onClose={() => setIsBusinessDropdownOpen(false)}
                  selectedBrand={filterBusiness}
                  onBrandSelect={handleBusinessSelect}
                />
              </div>
            </div>

            <ConversationFilters
              statusFilter={statusFilter}
              onStatusFilterChange={handleStatusFilterChange}
              statusReasonFilter={statusReasonFilter}
              onStatusReasonFilterChange={handleStatusReasonFilterChange}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onClearSearch={handleClearSearch}
            />
          </div>
        </div>
      </section>

      {/* Conversations Grid */}
      <div>
        {isLoading ? (
          <ConversationsGridSkeleton />
        ) : error ? (
          <div
            className="border-border bg-card rounded-lg border px-4 py-12 text-center shadow-[var(--od-shadow-1)]"
            role="alert"
          >
            <div className="bg-error-bg text-error mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg">
              <Icon name="AlertTriangle" className="h-8 w-8" />
            </div>
            <h3 className="text-error mb-2 text-lg font-medium">
              {t("errors.loadTitle")}
            </h3>
            <p className="text-muted-foreground break-words">
              {error.message || t("errors.loadDescription")}
            </p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="border-border bg-card rounded-lg border px-4 py-12 text-center shadow-[var(--od-shadow-1)]">
            <div className="bg-muted text-muted-foreground mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg">
              <Icon name="MessageCircle" className="h-8 w-8" />
            </div>
            <h3 className="text-foreground mb-2 text-lg font-medium">
              {searchQuery
                ? t("empty.noConversationsFound")
                : t("empty.noConversationsYet")}
            </h3>
            <p className="text-muted-foreground">
              {searchQuery ? t("empty.searchHint") : t("empty.defaultHint")}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {conversations.map((conversation) => (
                <ConversationCard
                  key={conversation.id}
                  conversation={conversation}
                  onViewChat={handleViewChat}
                  onViewOrder={handleViewOrder}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8">
                <div className="border-border bg-card sticky bottom-0 z-[var(--layer-sticky)] rounded-lg border p-4 shadow-[var(--od-shadow-2)]">
                  <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                    {/* Pagination Info */}
                    <div className="text-muted-foreground text-sm">
                      {t("pagination.showing", {
                        from: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                        to: Math.min(currentPage * ITEMS_PER_PAGE, totalItems),
                        total: totalItems,
                      })}
                    </div>

                    {/* Pagination Controls */}
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      maxVisiblePages={MAX_VISIBLE_PAGES}
                      disabled={isLoading}
                      className="mt-4 sm:mt-0"
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConversationsPage;
